import { forwardRef } from 'react';
import type { CalcResult, PlanInputs } from '../types';
import { fmtCeil, fmtPct } from '../lib/format';

interface Props {
  inputs: PlanInputs;
  result: CalcResult;
  onPrint: () => void;
  onExportDocx: () => void;
}

export const SystemValuesTable = forwardRef<HTMLDivElement, Props>(function SystemValuesTable(
  { inputs, result, onPrint, onExportDocx },
  ref
) {
  const wageBand = (() => {
    if (inputs.category === 'A_no') return '— （表明しないため不要）';
    switch (result.wageJudge.kind) {
      case 'plus_30':
        return '30円以上50円未満';
      case 'plus_50':
        return '50円以上';
      default:
        return '— （+30円未満のため加点対象外）';
    }
  })();

  const declare = inputs.category === 'A_no' ? 'いいえ' : 'はい';

  // 数値が未入力（0）の場合はプレースホルダ表示
  const placeholder = '半角数字で入力してください';
  const showOrPlaceholder = (n: number) =>
    n > 0 ? n.toLocaleString('ja-JP') : '';

  return (
    <div className="section" ref={ref} id="system-values">
      <h2>
        <span className="num">6</span>システム入力時の参考値
      </h2>
      <p className="section-desc">
        交付申請システム「計画数値入力」画面（電子申請システム）の見た目に合わせた参考値表です。下記の値をそのまま画面にコピーしてください。
      </p>

      <h3>計画数値（給与支給総額・従業員数）</h3>
      <div className="table-wrap">
        <table className="system-table">
          <thead>
            <tr>
              <th className="badge-cell">
                <span className="required-badge">必須</span>
              </th>
              <th>前年度実績値</th>
              <th>計画数値（1年度目）</th>
              <th>計画数値（2年度目）</th>
              <th>計画数値（3年度目）</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="row-label">給与支給総額</td>
              <td>
                <input
                  type="text"
                  className="system-input"
                  value={showOrPlaceholder(inputs.prevSalary)}
                  placeholder={placeholder}
                  readOnly
                />
              </td>
              <td>
                <input
                  type="text"
                  className="system-input"
                  value={showOrPlaceholder(inputs.salary1)}
                  placeholder={placeholder}
                  readOnly
                />
              </td>
              <td>
                <input
                  type="text"
                  className="system-input"
                  value={showOrPlaceholder(inputs.salary2)}
                  placeholder={placeholder}
                  readOnly
                />
              </td>
              <td>
                <input
                  type="text"
                  className="system-input"
                  value={showOrPlaceholder(inputs.salary3)}
                  placeholder={placeholder}
                  readOnly
                />
              </td>
            </tr>
            <tr>
              <td className="row-label">従業員数</td>
              <td>
                <input
                  type="text"
                  className="system-input"
                  value={inputs.prevEmployees > 0 ? inputs.prevEmployees.toString() : ''}
                  placeholder={placeholder}
                  readOnly
                />
              </td>
              <td>
                <input
                  type="text"
                  className="system-input"
                  value={inputs.emp1 > 0 ? inputs.emp1.toString() : ''}
                  placeholder={placeholder}
                  readOnly
                />
              </td>
              <td>
                <input
                  type="text"
                  className="system-input"
                  value={inputs.emp2 > 0 ? inputs.emp2.toString() : ''}
                  placeholder={placeholder}
                  readOnly
                />
              </td>
              <td>
                <input
                  type="text"
                  className="system-input"
                  value={inputs.emp3 > 0 ? inputs.emp3.toString() : ''}
                  placeholder={placeholder}
                  readOnly
                />
              </td>
            </tr>
            <tr className="calc-row">
              <td className="row-label">一人当たり給与支給総額</td>
              <td className="calc-cell">{fmtCeil(result.perEmp[0])}</td>
              <td className="calc-cell">{fmtCeil(result.perEmp[1])}</td>
              <td className="calc-cell">{fmtCeil(result.perEmp[2])}</td>
              <td className="calc-cell">{fmtCeil(result.perEmp[3])}</td>
            </tr>
            <tr className="calc-row">
              <td className="row-label">年平均成長率</td>
              <td className="calc-cell empty">—</td>
              <td className="calc-cell">{fmtPct(result.cagr1, 1)}</td>
              <td className="calc-cell">{fmtPct(result.cagr2, 1)}</td>
              <td className="calc-cell">{fmtPct(result.cagr3, 1)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3 style={{ marginTop: '1.2rem' }}>その他のシステム入力欄</h3>
      <div className="table-wrap">
        <table className="plan-table">
          <thead>
            <tr>
              <th>システム上の入力欄</th>
              <th>入力値</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>主たる事業場の所在地</td>
              <td className="auto">{inputs.prefectureName}</td>
            </tr>
            <tr>
              <td>主たる事業場の地域別最低賃金（自動表示）</td>
              <td className="auto">{inputs.regionMin.toLocaleString()} 円/時間</td>
            </tr>
            <tr>
              <td>主たる事業場における従業員の事業場内最低賃金</td>
              <td className="auto">{inputs.siteMin.toLocaleString()} 円/時間</td>
            </tr>
            <tr>
              <td>事業場内最低賃金の引上げ計画を、従業員へ表明しましたか？</td>
              <td className="auto">{declare}</td>
            </tr>
            <tr>
              <td>賃金引上げ計画における地域別最低賃金に対する賃上げ幅</td>
              <td className="auto">{wageBand}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="btn-row" style={{ marginTop: '0.8rem' }}>
        <button className="btn btn-purple" onClick={onPrint}>
          <i className="fas fa-print" /> 印刷／PDF保存
        </button>
        <button className="btn btn-purple" onClick={onExportDocx}>
          <i className="fas fa-file-word" /> Wordで案内書出力
        </button>
      </div>
    </div>
  );
});
